import { Box, styled, alpha, Accordion, AccordionSummary, AccordionDetails, lighten } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { LabelValue } from '@/components/_common';
import { ValidationDataError, ValidationDataUIAttributes } from '@/redux/slices/slice_types';

interface TitledListProps {
  data: ValidationDataError[];
  UIAttributes: ValidationDataUIAttributes;
  type: 'error' | 'warning';
}

interface TitledListItemProps {
  error: ValidationDataError;
  index: number;
  type: 'error' | 'warning';
  UIAttributes: ValidationDataUIAttributes;
}

const TitledList = ({ data, UIAttributes, type }: TitledListProps) => {
  return (
    <ListContainer color={type}>
      <Title>
        <IconTitle>
          <StyledPolarisIcon icon="error" type={type} />
          {type === 'error' ? 'Error' : 'Warning'}
          {data?.length > 1 && 's'}
          <Count>{data?.length || 0}</Count>
        </IconTitle>
        <DetailsText />
      </Title>
      {data && data.map((error, index) => <TitledListItem key={index} error={error} index={index} type={type} UIAttributes={UIAttributes} />)}
    </ListContainer>
  );
};
export default TitledList;

const StyledPolarisIcon = styled(PolarisIcon)<{ type: TitledListItemProps['type'] }>`
  color: ${({ theme, type }) => (Object.keys(theme.palette).includes(type) ? theme.palette[type].main : theme.palette.primary.main)};
`;

const TitledListItem = ({ error, index, type, UIAttributes }: TitledListItemProps) => {
  return (
    <NoMarginAccordion>
      <ListTitle expandIcon={<PolarisIcon icon="down" />} aria-controls={`panel${type + -+index}-content`} id={`panel${type + -+index}-header`}>
        {error.md_attribute}
      </ListTitle>
      <ListContent>
        {Object.entries(error).map(([key, value], i) => {
          if (!UIAttributes[key as keyof ValidationDataUIAttributes]?.display || key === 'md_attribute' || !value) return null;
          return (
            <Box key={i} sx={{ minWidth: '120px' }}>
              <LabelValue label={UIAttributes[key as keyof ValidationDataUIAttributes].label} color="primary">
                <Box fontSize="1.2rem">{value}</Box>
              </LabelValue>
            </Box>
          );
        })}
      </ListContent>
    </NoMarginAccordion>
  );
};

const ListContainer = styled(Accordion)<{ color: 'error' | 'warning' }>`
  background-color: ${({ theme, color }) => alpha(theme.palette[color].main, 0.03)};
  border: 1px solid ${({ theme, color }) => theme.palette[color].main};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  .Mui-expanded& {
    padding-top: 0;
  }
`;

const Title = styled(AccordionSummary)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${({ theme }) => theme.spacing(1)} 0 !important;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const IconTitle = styled('div')`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  flex-grow: 1;
  maring: 0 !important;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Count = styled('div')`
  font-size: 1rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
`;

const DetailsText = styled('div')`
  line-height: 2;
  &::before {
    content: 'Show Details';
  }
  .Mui-expanded &::before {
    content: 'Hide Details';
  }
`;

const NoMarginAccordion = styled(Accordion)`
  margin: -2px 0 0 0 !important;
  border-top: 3px solid ${({ theme }) => lighten(theme.palette.background.paper, 0.04)};
  border-bottom: 3px solid ${({ theme }) => lighten(theme.palette.background.paper, 0.04)};
  &:first-child {
    border-top: none;
  }
  &:last-child {
    border-bottom: none;
  }
`;

const ListTitle = styled(AccordionSummary)`
  background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.06)};
  margin: 0;
  font-size: 1.5rem;
  flex-direction: row !important;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(2)}!important;
  &.Mui-expanded {
    background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.1)};
  }
  & .MuiAccordionSummary-expandIconWrapper.Mui-expanded {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const ListContent = styled(AccordionDetails)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  display: flex;
  padding: ${({ theme }) => theme.spacing(2)}!important;
  column-gap: ${({ theme }) => theme.spacing(2)};
`;
